import React, { useState, useEffect, useRef } from 'react';
import DashboardLayout from '../../layouts/DashboardLayout';
import { api, useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  Database, 
  Download, 
  Upload, 
  Trash2, 
  RefreshCw, 
  HardDrive,
  FileJson,
  FileSpreadsheet,
  FileCode,
  AlertTriangle,
  CheckCircle,
  Loader2,
  Clock,
  Shield,
  Volume2,
  Mic,
  ListChecks,
  PenLine,
  FolderArchive,
  RotateCcw,
  Info,
  XCircle
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const BackupRestorePage = () => {
  const { user } = useAuth();
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, backup: null });
  const [exportFormat, setExportFormat] = useState('json');
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);
  const [selectedModule, setSelectedModule] = useState('tts');
  
  // Reset data states
  const [testCounts, setTestCounts] = useState({});
  const [resetting, setResetting] = useState(false);
  const [resetConfirmDialog, setResetConfirmDialog] = useState({ open: false, module: null });

  const modules = [
    { id: 'tts', name: 'Text to Speech', icon: Volume2, color: 'cyan' },
    { id: 'stt', name: 'Speech to Text', icon: Mic, color: 'emerald' },
    { id: 'mcq', name: 'MCQ Quiz', icon: ListChecks, color: 'yellow' },
    { id: 'ftb', name: 'Fill the Blanks', icon: PenLine, color: 'rose' },
  ];

  useEffect(() => {
    fetchBackups();
    if (user?.role === 'admin') {
      fetchTestCounts();
    }
  }, [user]);

  const fetchBackups = async () => {
    try {
      const res = await api.get('/backups');
      setBackups(res.data);
    } catch (err) {
      toast.error('Failed to load backups');
    } finally {
      setLoading(false);
    }
  };

  const fetchTestCounts = async () => {
    try {
      const res = await api.get('/tests/count');
      setTestCounts(res.data);
    } catch (err) {
      console.error('Failed to fetch test counts:', err);
    }
  };

  const resetTestData = async (moduleType) => {
    setResetting(true);
    try {
      const res = await api.delete(`/reset/tests/${moduleType}`);
      toast.success(res.data.message);
      fetchTestCounts();
      setResetConfirmDialog({ open: false, module: null });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset test data');
    } finally {
      setResetting(false);
    }
  };

  const createBackup = async () => {
    setCreating(true);
    try {
      const res = await api.post('/backups/create');
      toast.success(res.data.message);
      fetchBackups();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create backup');
    } finally {
      setCreating(false);
    }
  };

  const restoreBackup = async (filename) => {
    setRestoring(true);
    try {
      const res = await api.post(`/backups/restore/${filename}`);
      toast.success(res.data.message);
      setConfirmDialog({ open: false, backup: null });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to restore backup');
    } finally {
      setRestoring(false);
    }
  };

  const deleteBackup = async (filename) => {
    if (!window.confirm(`Are you sure you want to delete ${filename}?`)) return;
    try {
      await api.delete(`/backups/${filename}`);
      toast.success('Backup deleted');
      fetchBackups();
    } catch (err) {
      toast.error('Failed to delete backup');
    }
  };

  const downloadBackup = (filename) => {
    const token = localStorage.getItem('vocalab_token');
    const url = `${BACKEND_URL}/api/backups/download/${filename}?token=${token}`;
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const exportTests = async (moduleType, format) => {
    try {
      // Use api instance with authentication to get the file
      const response = await api.get(`/export/tests/${moduleType}?format=${format}`, {
        responseType: 'blob'
      });
      
      // Create a download link
      const blob = new Blob([response.data], { 
        type: format === 'json' ? 'application/json' : 
              format === 'csv' ? 'text/csv' : 'application/sql' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from content-disposition header or generate one
      const contentDisposition = response.headers['content-disposition'];
      let filename = `${moduleType}_tests.${format}`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename=([^;]+)/);
        if (filenameMatch) {
          filename = filenameMatch[1].replace(/"/g, '');
        }
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Exported ${moduleType.toUpperCase()} tests as ${format.toUpperCase()}`);
    } catch (err) {
      console.error('Export error:', err);
      toast.error(err.response?.data?.detail || 'Export failed');
    }
  };

  const importTests = async (moduleType, file) => {
    if (!file) return;
    
    setImporting(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const res = await api.post(`/import/tests/${moduleType}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      importTests(selectedModule, file);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-in">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
              <Database className="h-7 w-7 text-indigo-600" />
              Backup & Restore
            </h1>
            <p className="text-slate-500 dark:text-slate-400 mt-1">
              Manage database backups and import/export test data
            </p>
          </div>
        </div>

        <Tabs defaultValue="database" className="space-y-6">
          <TabsList className={`grid max-w-xl ${user?.role === 'admin' ? 'grid-cols-3' : 'grid-cols-2'}`}>
            <TabsTrigger value="database" className="flex items-center gap-2">
              <HardDrive className="h-4 w-4" />
              Database Backup
            </TabsTrigger>
            <TabsTrigger value="tests" className="flex items-center gap-2">
              <FolderArchive className="h-4 w-4" />
              Test Data
            </TabsTrigger>
            {user?.role === 'admin' && (
              <TabsTrigger value="reset" className="flex items-center gap-2">
                <XCircle className="h-4 w-4" />
                Reset Data
              </TabsTrigger>
            )}
          </TabsList>

          {/* Database Backup Tab */}
          <TabsContent value="database" className="space-y-6">
            {/* Info Card */}
            <Card className="border-blue-200 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-800">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <Info className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5" />
                  <div>
                    <p className="font-medium text-blue-900 dark:text-blue-200">Database Backup Information</p>
                    <ul className="text-sm text-blue-700 dark:text-blue-300 mt-2 space-y-1">
                      <li>• Backups include all user data, tests, questions, and settings</li>
                      <li>• Media files (videos, audio) are NOT included in backups</li>
                      <li>• Maximum 3 backups are stored; oldest is auto-deleted</li>
                      <li>• Only admins can restore backups</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Create Backup */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="h-5 w-5 text-emerald-600" />
                  Create New Backup
                </CardTitle>
                <CardDescription>
                  Create a full database backup in SQL format
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button 
                  onClick={createBackup} 
                  disabled={creating}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  {creating ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Database className="h-4 w-4 mr-2" />
                  )}
                  Create Backup Now
                </Button>
              </CardContent>
            </Card>

            {/* Backups List */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <FolderArchive className="h-5 w-5 text-indigo-600" />
                    Available Backups
                  </CardTitle>
                  <CardDescription>
                    Maximum 3 backups stored
                  </CardDescription>
                </div>
                <Button variant="outline" size="sm" onClick={fetchBackups}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Refresh
                </Button>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
                  </div>
                ) : backups.length === 0 ? (
                  <div className="text-center py-8">
                    <Database className="h-12 w-12 mx-auto text-slate-300 mb-4" />
                    <p className="text-slate-500">No backups available</p>
                    <p className="text-sm text-slate-400">Create your first backup above</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {backups.map((backup, idx) => (
                      <div 
                        key={backup.filename}
                        className={`flex items-center justify-between p-4 rounded-xl border-2 transition-all ${
                          idx === 0 
                            ? 'border-emerald-200 bg-emerald-50 dark:bg-emerald-900/20 dark:border-emerald-800' 
                            : 'border-slate-200 bg-slate-50 dark:bg-slate-800 dark:border-slate-700'
                        }`}
                      >
                        <div className="flex items-center gap-4">
                          <div className={`p-3 rounded-xl ${idx === 0 ? 'bg-emerald-100 dark:bg-emerald-900/50' : 'bg-slate-200 dark:bg-slate-700'}`}>
                            <FileCode className={`h-6 w-6 ${idx === 0 ? 'text-emerald-600' : 'text-slate-500'}`} />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900 dark:text-white flex items-center gap-2">
                              {backup.filename}
                              {idx === 0 && (
                                <span className="px-2 py-0.5 text-xs bg-emerald-500 text-white rounded-full">Latest</span>
                              )}
                            </p>
                            <div className="flex items-center gap-4 text-sm text-slate-500">
                              <span className="flex items-center gap-1">
                                <Clock className="h-3.5 w-3.5" />
                                {/* Display time in IST without browser conversion */}
                                {(() => {
                                  const date = new Date(backup.created_at);
                                  // Format as IST (the backend already sends IST time)
                                  return date.toLocaleString('en-IN', { 
                                    timeZone: 'Asia/Kolkata',
                                    day: '2-digit',
                                    month: '2-digit', 
                                    year: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                    second: '2-digit',
                                    hour12: true
                                  });
                                })()}
                              </span>
                              <span>{backup.size_formatted}</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => downloadBackup(backup.filename)}
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => setConfirmDialog({ open: true, backup })}
                            className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                          >
                            <RotateCcw className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => deleteBackup(backup.filename)}
                            className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Test Data Tab */}
          <TabsContent value="tests" className="space-y-6">
            {/* Info Card */}
            <Card className="border-purple-200 bg-purple-50 dark:bg-purple-900/20 dark:border-purple-800">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <Info className="h-5 w-5 text-purple-600 dark:text-purple-400 mt-0.5" />
                  <div>
                    <p className="font-medium text-purple-900 dark:text-purple-200">Test Data Import/Export</p>
                    <ul className="text-sm text-purple-700 dark:text-purple-300 mt-2 space-y-1">
                      <li>• Export tests in JSON, CSV, or SQL format</li>
                      <li>• Import tests from JSON, CSV, or SQL files</li>
                      <li>• Each module (TTS, STT, MCQ, FTB) has separate data</li>
                      <li>• Use CSV for easy editing in Excel/Spreadsheet</li>
                      <li>• Use SQL for direct database restore compatibility</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Module Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {modules.map((module) => {
                const Icon = module.icon;
                return (
                  <Card key={module.id} className="overflow-hidden">
                    <CardHeader className={`bg-gradient-to-r from-${module.color}-500/10 to-${module.color}-600/10 border-b`}>
                      <CardTitle className="flex items-center gap-2 text-lg">
                        <Icon className={`h-5 w-5 text-${module.color}-600`} />
                        {module.name}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-4 space-y-4">
                      {/* Export Section */}
                      <div>
                        <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Export Tests</p>
                        <div className="flex gap-2">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => exportTests(module.id, 'json')}
                            className="flex-1"
                          >
                            <FileJson className="h-4 w-4 mr-1" />
                            JSON
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => exportTests(module.id, 'csv')}
                            className="flex-1"
                          >
                            <FileSpreadsheet className="h-4 w-4 mr-1" />
                            CSV
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => exportTests(module.id, 'sql')}
                            className="flex-1"
                          >
                            <FileCode className="h-4 w-4 mr-1" />
                            SQL
                          </Button>
                        </div>
                      </div>
                      
                      {/* Import Section */}
                      <div>
                        <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Import Tests</p>
                        <input
                          type="file"
                          accept=".json,.csv,.sql"
                          className="hidden"
                          id={`import-${module.id}`}
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) importTests(module.id, file);
                            e.target.value = '';
                          }}
                        />
                        <label htmlFor={`import-${module.id}`}>
                          <Button 
                            variant="outline" 
                            className="w-full cursor-pointer"
                            asChild
                          >
                            <span>
                              {importing ? (
                                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                              ) : (
                                <Upload className="h-4 w-4 mr-2" />
                              )}
                              Import JSON/CSV/SQL
                            </span>
                          </Button>
                        </label>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* CSV Format Guide */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileSpreadsheet className="h-5 w-5 text-emerald-600" />
                  Import Format Guide
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <p className="font-medium text-slate-700 dark:text-slate-300 mb-2">CSV/JSON Required Fields:</p>
                    <div className="bg-slate-100 dark:bg-slate-800 rounded-lg p-4 font-mono text-sm overflow-x-auto">
                      <p>question_text, options, correct_option, correct_answers, explanation</p>
                    </div>
                  </div>
                  
                  <div>
                    <p className="font-medium text-slate-700 dark:text-slate-300 mb-2">MCQ Example (CSV):</p>
                    <div className="bg-slate-100 dark:bg-slate-800 rounded-lg p-4 font-mono text-xs overflow-x-auto">
                      <p>question_text,options,correct_option,correct_answers,explanation</p>
                      <p>"What is 2+2?","[""2"",""3"",""4"",""5""]",2,,"Basic math"</p>
                    </div>
                  </div>
                  
                  <div>
                    <p className="font-medium text-slate-700 dark:text-slate-300 mb-2">FTB Example (CSV):</p>
                    <div className="bg-slate-100 dark:bg-slate-800 rounded-lg p-4 font-mono text-xs overflow-x-auto">
                      <p>question_text,options,correct_option,correct_answers,explanation</p>
                      <p>"The sun ___ in the east.",,"","[""rises""]","Geography fact"</p>
                    </div>
                  </div>
                  
                  <div>
                    <p className="font-medium text-slate-700 dark:text-slate-300 mb-2">SQL Format:</p>
                    <div className="bg-slate-100 dark:bg-slate-800 rounded-lg p-4 font-mono text-xs overflow-x-auto">
                      <p className="text-slate-500">-- Use exported SQL files from this app</p>
                      <p>INSERT INTO questions (id, module_type, question_text, ...) VALUES (...);</p>
                    </div>
                    <p className="text-xs text-slate-500 mt-2">SQL import reads INSERT INTO questions statements from exported .sql files</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Reset Data Tab - Admin Only */}
          {user?.role === 'admin' && (
            <TabsContent value="reset" className="space-y-6">
              {/* Warning Card */}
              <Card className="border-red-200 bg-red-50 dark:bg-red-900/20 dark:border-red-800">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400 mt-0.5" />
                    <div>
                      <p className="font-medium text-red-900 dark:text-red-200">⚠️ Danger Zone - Reset Test Data</p>
                      <ul className="text-sm text-red-700 dark:text-red-300 mt-2 space-y-1">
                        <li>• This action will <strong>permanently delete</strong> all questions, tests, and attempts for the selected module</li>
                        <li>• Test numbering will restart from 1 for newly created tests</li>
                        <li>• This action <strong>cannot be undone</strong> - create a backup first!</li>
                        <li>• Only admins can perform this action</li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Module Reset Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[...modules, { id: 'all', name: 'All Modules', icon: Database, color: 'red' }].map((module) => {
                  const Icon = module.icon;
                  const count = module.id === 'all' 
                    ? {
                        questions: Object.values(testCounts).reduce((sum, m) => sum + (m?.questions || 0), 0),
                        tests: Object.values(testCounts).reduce((sum, m) => sum + (m?.tests || 0), 0),
                        attempts: Object.values(testCounts).reduce((sum, m) => sum + (m?.attempts || 0), 0)
                      }
                    : testCounts[module.id] || { questions: 0, tests: 0, attempts: 0 };
                  
                  return (
                    <Card key={module.id} className={`${module.id === 'all' ? 'border-red-300 dark:border-red-700' : ''}`}>
                      <CardHeader className="pb-3">
                        <CardTitle className="flex items-center gap-2 text-lg">
                          <Icon className={`h-5 w-5 ${module.id === 'all' ? 'text-red-500' : `text-${module.color}-500`}`} />
                          {module.name}
                          {module.id === 'all' && (
                            <span className="ml-auto text-xs bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300 px-2 py-1 rounded-full">
                              DANGER
                            </span>
                          )}
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        {/* Stats */}
                        <div className="grid grid-cols-3 gap-2 text-center">
                          <div className="bg-slate-100 dark:bg-slate-800 rounded-lg p-3">
                            <p className="text-2xl font-bold text-slate-900 dark:text-white">{count.questions}</p>
                            <p className="text-xs text-slate-500">Questions</p>
                          </div>
                          <div className="bg-slate-100 dark:bg-slate-800 rounded-lg p-3">
                            <p className="text-2xl font-bold text-slate-900 dark:text-white">{count.tests}</p>
                            <p className="text-xs text-slate-500">Tests</p>
                          </div>
                          <div className="bg-slate-100 dark:bg-slate-800 rounded-lg p-3">
                            <p className="text-2xl font-bold text-slate-900 dark:text-white">{count.attempts}</p>
                            <p className="text-xs text-slate-500">Attempts</p>
                          </div>
                        </div>
                        
                        {/* Reset Button */}
                        <Button 
                          variant={module.id === 'all' ? 'destructive' : 'outline'}
                          className={`w-full ${module.id !== 'all' ? 'text-red-600 border-red-300 hover:bg-red-50 hover:border-red-400' : ''}`}
                          onClick={() => setResetConfirmDialog({ open: true, module: module })}
                          disabled={count.questions === 0 && count.tests === 0}
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Reset {module.name}
                        </Button>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </TabsContent>
          )}
        </Tabs>

        {/* Reset Confirmation Dialog */}
        <Dialog open={resetConfirmDialog.open} onOpenChange={(open) => setResetConfirmDialog({ open, module: null })}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <XCircle className="h-5 w-5" />
                Confirm Reset - {resetConfirmDialog.module?.name}
              </DialogTitle>
              <DialogDescription>
                Are you sure you want to reset all test data for this module?
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                <p className="text-red-800 dark:text-red-200 font-medium">
                  ⚠️ This will permanently delete:
                </p>
                <ul className="text-sm text-red-700 dark:text-red-300 mt-2 space-y-1">
                  <li>• All questions in this module</li>
                  <li>• All tests created for this module</li>
                  <li>• All student attempts and scores</li>
                  <li>• Test numbering will restart from 1</li>
                </ul>
              </div>
              {resetConfirmDialog.module && (
                <div className="mt-4 p-3 bg-slate-100 dark:bg-slate-800 rounded-lg">
                  <p className="text-sm text-slate-600 dark:text-slate-300">
                    <strong>Module:</strong> {resetConfirmDialog.module.name}
                  </p>
                  <p className="text-sm text-slate-600 dark:text-slate-300">
                    <strong>Questions to delete:</strong> {
                      resetConfirmDialog.module.id === 'all'
                        ? Object.values(testCounts).reduce((sum, m) => sum + (m?.questions || 0), 0)
                        : testCounts[resetConfirmDialog.module.id]?.questions || 0
                    }
                  </p>
                  <p className="text-sm text-slate-600 dark:text-slate-300">
                    <strong>Tests to delete:</strong> {
                      resetConfirmDialog.module.id === 'all'
                        ? Object.values(testCounts).reduce((sum, m) => sum + (m?.tests || 0), 0)
                        : testCounts[resetConfirmDialog.module.id]?.tests || 0
                    }
                  </p>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setResetConfirmDialog({ open: false, module: null })}>
                Cancel
              </Button>
              <Button 
                variant="destructive"
                onClick={() => resetTestData(resetConfirmDialog.module?.id)}
                disabled={resetting}
              >
                {resetting ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Trash2 className="h-4 w-4 mr-2" />
                )}
                Yes, Reset Data
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Restore Confirmation Dialog */}
        <Dialog open={confirmDialog.open} onOpenChange={(open) => setConfirmDialog({ open, backup: null })}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-amber-600">
                <AlertTriangle className="h-5 w-5" />
                Confirm Database Restore
              </DialogTitle>
              <DialogDescription>
                Are you sure you want to restore the database from this backup?
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
                <p className="text-amber-800 dark:text-amber-200 font-medium">
                  ⚠️ Warning: This action will overwrite the current database!
                </p>
                <ul className="text-sm text-amber-700 dark:text-amber-300 mt-2 space-y-1">
                  <li>• All current data will be replaced</li>
                  <li>• This cannot be undone</li>
                  <li>• Create a backup first if needed</li>
                </ul>
              </div>
              {confirmDialog.backup && (
                <div className="mt-4 p-3 bg-slate-100 dark:bg-slate-800 rounded-lg">
                  <p className="text-sm text-slate-600 dark:text-slate-300">
                    <strong>Backup:</strong> {confirmDialog.backup.filename}
                  </p>
                  <p className="text-sm text-slate-600 dark:text-slate-300">
                    <strong>Created:</strong> {new Date(confirmDialog.backup.created_at).toLocaleString()}
                  </p>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setConfirmDialog({ open: false, backup: null })}>
                Cancel
              </Button>
              <Button 
                variant="destructive"
                onClick={() => restoreBackup(confirmDialog.backup?.filename)}
                disabled={restoring}
              >
                {restoring ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <RotateCcw className="h-4 w-4 mr-2" />
                )}
                Restore Database
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default BackupRestorePage;
